import { fetchExperiencesById } from 'actions/ExperienceActions'
import { take } from 'lib/array/arrayUtils'
import { mapTravelStylesToTargetAudience } from 'lib/experiences/experienceUtils'
import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperienceSearchList, { type ExperienceSearchListState } from './useExperienceSearchList'

interface SearchProps {
  placeId?: string
  placeIdToIgnore?: string
  limit?: number
  sortBy?: Experiences.SortTypes
  excludeIds?: Array<string>
  campaigns?: Array<string>
  randomize?: boolean
  enabled?: boolean
  from?: string
  to?: string
}

function useExperienceSearch(
  searchProps: SearchProps,
  filterProps: Experiences.SearchFilters = {},
): [ExperienceSearchListState, Array<App.ExperienceOffer | undefined>] {
  const {
    placeId,
    placeIdToIgnore,
    limit = 8,
    sortBy,
    excludeIds = [],
    campaigns,
    randomize,
    enabled = true,
    from,
    to,
  } = searchProps
  const {
    categoryCodes,
    priceLte,
    priceGte,
    luxPlusFeatures,
    leExclusive,
    collections,
    minRating,
    travelStyles,
    featured,
  } = filterProps
  const dispatch = useAppDispatch()

  const targetAudience = travelStyles?.length ? mapTravelStylesToTargetAudience(travelStyles) : undefined
  const hasFreeCancellation = featured?.includes('free-cancellation') || undefined
  const hasInstantConfirmation = featured?.includes('instant-confirmation') || undefined
  const isGift = featured?.includes('is-gift') || undefined

  const experienceList = useExperienceSearchList({ categoryCodes, placeId, placeIdToIgnore, sortBy, excludeIds, priceLte, priceGte, campaigns, randomize, enabled, luxPlusFeatures, leExclusive, collections, from, to, minRating, targetAudience, hasFreeCancellation, hasInstantConfirmation, isGift })
  const experiences = useAppSelector((state) => state.experience.experiences)

  const experienceItems = useMemo(() => {
    if (experienceList) {
      const ids = take(experienceList.ids, limit)
      return ids.map(id => experiences[id])
    }
    return []
  }, [experienceList, limit, experiences])

  useEffect(() => {
    if (experienceList?.ids) {
      const ids = take(experienceList.ids, limit)
      dispatch(fetchExperiencesById(...ids))
    }
  }, [experienceList?.ids, limit, dispatch])

  return [
    experienceList,
    experienceItems,
  ]
}

export default useExperienceSearch
